import React, { useState, useEffect, SyntheticEvent } from "react";
import axios from 'axios';
import { NavLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import AltRouteOutlined from '@mui/icons-material/AltRouteOutlined';
import ClearAllOutlined from '@mui/icons-material/ClearAllOutlined';
import HealthAndSafetyOutlined from '@mui/icons-material/HealthAndSafetyOutlined';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import PlaylistAddOutlined from '@mui/icons-material/PlaylistAddOutlined';
import PointOfSaleOutlined from '@mui/icons-material/PointOfSaleOutlined';
import { EmptyState, LoadingButton, MoneyField, PageHeader } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import { ExpenseType } from '../../../models/expense-type';
import { OperationType } from '../../../models/operation-type';
import RouteDetailModal from './modal/RouteDetailModal';
import { useSelection } from '../../../components/context/SelectionContext';
import RouteCashBoxModal from "./modal/RouteCashBoxModal";
import { nowOperationalDateTimeString } from "../../../utils/operationalDateTime";
import PermissionButton from "../../../components/common/PermissionButton";
import { useResourcePermissions } from "../../../hooks/useResourcePermissions";
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { companyCurrency, companyLocale, formatCurrency } from '../../../utils/format/currency';
import RowAction from '../components/RowAction';
import OfficeRouteSelects from '../components/OfficeRouteSelects';
import { formActionsSx, formGridSx, formMaxWidthSx, pageSx, sectionSx } from '../components/layout';

type CajaMode = 'cobro' | 'seguro';

const CAJA_SEGURO_TYPE_NAME = 'Caja Seguro';
const CAJA_COBRO_TYPE_NAME = 'Caja Cobro';
const SEGUROS_TYPE_EXPENSE_NAME = 'SEGUROS';
const OFICINA_TYPE_EXPENSE_NAME = 'Gastos Oficina';

const getCashBoxByMode = (route: Route | null | undefined, mode: CajaMode) => {
  if (!route?.cashBoxes?.length) return null;
  const typeName = mode === 'seguro' ? CAJA_SEGURO_TYPE_NAME : CAJA_COBRO_TYPE_NAME;
  return route.cashBoxes.find((b: any) => b.type?.name === typeName) || (mode === 'cobro' ? route.cashBoxes[0] : null);
};

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const ExpenseNew: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle('expenses.newTitle');
  const [saving, setSaving] = useState(false);
  const { canView: canViewMainCashMovements } = useResourcePermissions('/loans/cash-box-expenses/new');
  const { canAdjustBox, canRegisterExpenses, canManageInsuranceBox } = useResourcePermissions('/loans/expenses/new');
  const [cajaMode, setCajaMode] = useState<CajaMode>('cobro');
  const [insuranceTypeExpenseId, setInsuranceTypeExpenseId] = useState<number | null>(null);
  const [cashBoxId, setCashBoxId] = useState('');
  const [balance, setBalance] = useState('');
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [cashBox, setCashBox] = useState<any[]>([]);
  const [officeCashBoxId, setOfficeCashBoxId] = useState<number | null>(null);
  const [affectMainBox, setAffectMainBox] = useState(false);
  const [tipogasto, setTipoGasto] = useState('');
  const [value, setValue] = useState('');
  const [description, setDescripcion] = useState('');
  const navigate = useNavigate();
  const [expenseTypeId, setExpenseTypeId] = useState<number | null>(null);
  const [operation, setOperation] = useState<OperationType[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [movementDate, setMovementDate] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpenBox, setModalOpenBox] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [expenses, setExpenses] = useState<ExpenseType[]>([]);
  const [expensesBox, setExpensesBox] = useState<ExpenseType[]>([]);
  const [routeExpenses, setRouteExpenses] = useState<{ [key: number]: { [key: number]: { value: number, cashBoxId: number, operationTypeId: number } } }>({});

  const [routeCheckbox, setRouteCheckbox] = useState<{ [key: number]: boolean }>({});
  const { office_id: officeId, setOffice_id: setOfficeId, user_id: userId, setUser_id: setUserId } = useSelection();
  const [selectedExpensesByRoute, setSelectedExpensesByRoute] = useState<{ [key: number]: number[] }>({});

  // Gastos seleccionados por ruta
  const handleSelectedExpensesChange = (routeId: number, expenseIds: number[]) => {
    setSelectedExpensesByRoute((prev) => ({
      ...prev,
      [routeId]: expenseIds,
    }));
  };
  const handleOpenModal = (route: Route) => {
    if (routeCheckbox[route.id]) {
      setSelectedRoute(route);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRoute(null);
  };
  const OpenModalBox = (route: Route) => {
    if (routeCheckbox[route.id]) {
      setSelectedRoute(route);
      setModalOpenBox(true);
    }
  };
  const CloseModalBox = () => {
    setModalOpenBox(false);
    setSelectedRoute(null);
  };

  // Valor de un gasto en una ruta
  const handleSetExpenseValue = (routeId: number, expenseId: number, value: number, boxId: number, typeOperationId: number) => {
    setRouteExpenses((prev) => ({
      ...prev,
      [routeId]: {
        ...prev[routeId],
        [expenseId]: {
          value,
          cashBoxId: boxId,
          operationTypeId: typeOperationId,
        },
      },
    }));
  };
  // Valor de un ajuste de caja en una ruta (modal RouteCashBoxModal)
  const SetExpenseValueBox = (routeId: number, expenseId: number, value: number, boxId: number, typeOperationId: number) => {
    setRouteExpenses((prev) => ({
      ...prev,
      [routeId]: {
        ...prev[routeId],
        [expenseId]: {
          value,
          cashBoxId: boxId,
          operationTypeId: typeOperationId
        },
      },
    }));
  };

  const handleClearExpenses = (routeId: number) => {
    setRouteExpenses(prev => ({
      ...prev,
      [routeId]: {}
    }));
    setRouteCheckbox(prev => ({
      ...prev,
      [routeId]: false
    }));
  };

  const handleCheckboxChange = (routeId: number) => {
    setRouteCheckbox(prev => {
      const newState = !prev[routeId];
      if (!newState) {
        handleClearExpenses(routeId);
      }
      return {
        ...prev,
        [routeId]: newState
      };
    });
  };
  const handleToggleAllRoutes = () => {
    const allChecked = Object.values(routeCheckbox).every((isChecked) => isChecked);
    const newRouteCheckbox = { ...routeCheckbox };
    officeRoutes.forEach((route) => {
      newRouteCheckbox[route.id] = !allChecked;
    });
    setRouteCheckbox(newRouteCheckbox);
    if (allChecked) {
      setRouteExpenses({});
    }
  };
  const validateTotal = () => {
    const totalRouteExpenses = Object.values(routeExpenses).reduce((acc, expenses) => {
      return acc + Object.values(expenses).reduce((sum, expense) => sum + expense.value, 0);
    }, 0);

    return totalRouteExpenses === parseInt(value);
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!officeId || !value || !routeExpenses) {
      showAlert(t('expenses.requiredFields'), 'warning');
      return;
    }
    if (!validateTotal()) {
      showAlert(t('expenses.totalMismatch'), 'warning');
      return;
    }
    showLoading();
    setSaving(true);
    const filteredRouteExpenses = Object.fromEntries(
      Object.entries(routeExpenses)
        .map(([routeId, expenses]) => {
          const normalizedExpenses = Object.fromEntries(
            Object.entries(expenses)
              .filter(([, expense]) => expense.value > 0)
              .map(([expenseId, expense]) => [
                expenseId,
                {
                  value: expense.value,
                  originCashBoxId: expense.cashBoxId,
                  operationTypeId: expense.operationTypeId,
                },
              ])
          );

          if (Object.keys(normalizedExpenses).length === 0) {
            return null;
          }

          const expensesValues = Object.values(normalizedExpenses) as Array<{
            originCashBoxId: number;
          }>;
          const boxOriginId = expensesValues[0]?.originCashBoxId;

          if (boxOriginId === undefined || boxOriginId === null) {
            return null;
          }

          return [boxOriginId.toString(), normalizedExpenses];
        })
        .filter((entry): entry is [string, Record<string, { value: number; originCashBoxId: number; operationTypeId: number }>] => entry !== null)
    );

    const expenseDate = movementDate
      ? `${String(movementDate).slice(0, 10)} ${nowOperationalDateTimeString().slice(11)}`
      : nowOperationalDateTimeString();
    const formData = {
      date: expenseDate,
      description: description,
      destinationCashBoxId: affectMainBox ? officeCashBoxId : null,
      value: parseInt(value),
      routeExpenses: filteredRouteExpenses,
      userId: userId,
    };
    // //console.log("formData", formData);
    try {
      // const response = await axios.post('/cash-box-expenses', formData);
      const response = await axios.post('/cash-movements/route', formData);
      showAlert(t('expenses.savedOk'), 'success');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate('/loans/expenses');
    } catch (error) {
      showAlert(t('expenses.saveError'), 'error');
    } finally {
      setSaving(false);
      hideLoading();
    }
  };

  const fetchBalance = async (officeId: string) => {
    try {
      const { data } = await axios.get(`/offices/${officeId}/cash-box`);
      setBalance(data.data[0].balance);
      setOfficeCashBoxId(data.data[0].id);
    } catch (error) {
      showAlert(t('expenses.balanceError'), 'error');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const [{ data: userData }, { data: officesData }, { data: operationData }] = await Promise.all([
          axios.get('user'),
          axios.get('offices'),
          axios.get('typeoperation'),
        ]);
        setUserId(userData.data.id);
        setOffices(officesData.data);
        const filteredOperations = operationData.data.filter((op: OperationType) => op.id === 2);
        setOperation(filteredOperations);
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } catch (error) {
        showAlert(t('common.loadError'), 'error');
      } finally {
        hideLoading();
      }
    };
    fetchData();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      if (officeId) {
        showLoading();
        try {
          const [{ data: boxData }, { data: routesData }] = await Promise.all([
            axios.get(`/offices/${officeId}/cash-box`),
            axios.get(`offices/${officeId}/routes`),
          ]);

          if (boxData.data.length > 0) {
            setCashBox(boxData.data[0]);
            setCashBoxId(boxData.data[0].id);
            await fetchBalance(officeId);
          } else {
            showAlert(t('expenses.noOfficeCashBox'), 'warning');
          }
          setOfficeRoutes(routesData);
          if (routesData.length === 0) {
            showAlert(t('expenses.noRoutes'), 'warning');
          }
        } catch (error) {
          showAlert(t('common.loadError'), 'error');
        } finally {
          hideLoading();
        }
      }
    };
    fetchData();
  }, [officeId]);

  // Resuelve el id de la categoría "SEGUROS" (puede variar por cliente, no se hardcodea).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('expense-categories');
        const seguros = (data?.data || []).find(
          (te: { id: number; name: string }) => te.name === SEGUROS_TYPE_EXPENSE_NAME
        );
        setInsuranceTypeExpenseId(seguros ? seguros.id : null);
      } catch (error) {
        // Silencioso: si no existe la categoría, el toggle de Caja Seguro simplemente no tendrá conceptos.
      }
    })();
  }, []);

  useEffect(() => {
    if (cajaMode === 'seguro' && !insuranceTypeExpenseId) {
      setExpenses([]);
      return;
    }
    const typeExpenseId = cajaMode === 'seguro' ? insuranceTypeExpenseId : 3;
    (async () => {
      try {
        const { data } = await axios.post('expense-types/search', { categoryId: typeExpenseId, operationTypeId: 2 });
        setExpenses(data.data);
        if (cajaMode === 'cobro') {
          const { data: dataBox } = await axios.get(`expense-categories/3/box-adjustments`);
          setExpensesBox(dataBox.data);
        } else {
          setExpensesBox([]);
        }
        setExpenseTypeId(null);
        setTipoGasto('');
      } catch (error) {
        showAlert(t('expenses.loadExpenseTypesError'), 'error');
      }
    })();
  }, [cajaMode, insuranceTypeExpenseId]);

  const allRoutesChecked = officeRoutes.length > 0 && Object.values(routeCheckbox).length > 0 && Object.values(routeCheckbox).every((isChecked) => isChecked);
  const someRoutesChecked = !allRoutesChecked && Object.values(routeCheckbox).some((isChecked) => isChecked);
  const routeTotal = (routeId: number) =>
    routeCheckbox[routeId] && routeExpenses[routeId]
      ? Object.values(routeExpenses[routeId]).reduce((a, b) => a + b.value, 0)
      : 0;
  const distributed = officeRoutes.reduce((sum, route) => sum + routeTotal(route.id), 0);

  return (
    <Box sx={pageSx}>
      <PageHeader
        title={t('expenses.newTitle')}
        description={t('expenses.newDescription')}
        breadcrumbs={[
          { label: t('expenses.listTitle'), href: '#/loans/expenses' },
          { label: t('expenses.newTitle') },
        ]}
        actions={
          <>
            <Button variant="outlined" startIcon={<ListAltOutlined />} component={NavLink} to="/loans/expenses">
              {t('expenses.viewMovements')}
            </Button>
            <PermissionButton
              allowed={canViewMainCashMovements}
              deniedTooltip={t('expenses.noPermissionMainCashBox')}
              variant="outlined"
              startIcon={<AccountBalanceOutlined />}
              component={NavLink}
              to="/loans/cash-box-expenses/new"
            >
              {t('expenses.mainCashBox')}
            </PermissionButton>
          </>
        }
      />
      <Box component="form" onSubmit={submit} sx={{ ...formMaxWidthSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper variant="outlined" sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="h6">{t('expenses.sectionData')}</Typography>
            {canManageInsuranceBox ? (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={cajaMode}
                onChange={(_, newMode) => {
                  if (newMode) setCajaMode(newMode);
                }}
                aria-label={t('expenses.cashBoxKind')}
              >
                <ToggleButton value="cobro">
                  <PointOfSaleOutlined fontSize="small" sx={{ mr: 0.5 }} /> {t('expenses.routeCashBox')}
                </ToggleButton>
                <ToggleButton value="seguro">
                  <HealthAndSafetyOutlined fontSize="small" sx={{ mr: 0.5 }} /> {t('expenses.insuranceCashBox')}
                </ToggleButton>
              </ToggleButtonGroup>
            ) : null}
          </Box>
          <Box sx={formGridSx}>
            <OfficeRouteSelects offices={offices} officeId={officeId} onOfficeChange={setOfficeId} required />
            <TextField
              fullWidth
              size="small"
              label={t('expenses.officeBalance')}
              value={formatCurrency(balance || 0)}
              InputProps={{ readOnly: true }}
            />
            <MoneyField
              fullWidth
              size="small"
              required
              label={t('expenses.value')}
              value={value === '' ? null : Number(value)}
              onChange={(v) => setValue(v == null ? '' : String(v))}
              currency={companyCurrency()}
              locale={companyLocale()}
            />
            <TextField
              fullWidth
              size="small"
              label={t('expenses.description')}
              placeholder={t('expenses.descriptionPlaceholder')}
              onChange={(event) => setDescripcion(event.target.value)}
              inputProps={{ maxLength: 100 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={affectMainBox}
                  onChange={(event) => setAffectMainBox(event.target.checked)}
                  disabled={!officeCashBoxId}
                />
              }
              label={t('expenses.affectMainCashBox')}
            />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="h6">{t('expenses.sectionRoutes')}</Typography>
            <Typography variant="body2" color={value !== '' && distributed !== Number(value) ? 'warning.main' : 'text.secondary'}>
              {t('expenses.distributedOf', { done: formatCurrency(distributed), total: formatCurrency(value || 0) })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('expenses.routesHelp')}
          </Typography>
          {officeRoutes && officeRoutes.length > 0 ? (
            <>
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Checkbox
                    checked={allRoutesChecked}
                    indeterminate={someRoutesChecked}
                    onChange={() => handleToggleAllRoutes()}
                  />
                }
                label={t('expenses.toggleAllRoutes')}
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
                  gap: 2,
                }}
              >
                {officeRoutes.map((route: Route) => {
                  const checked = routeCheckbox[route.id] || false;
                  return (
                    <Paper
                      key={route.id}
                      variant="outlined"
                      sx={{ p: 1.5, minWidth: 0, borderColor: checked ? 'primary.main' : 'divider' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <FormControlLabel
                          sx={{ flex: 1, minWidth: 0, mr: 0, '& .MuiFormControlLabel-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                          control={<Checkbox checked={checked} onChange={() => handleCheckboxChange(route.id)} />}
                          label={route.name}
                        />
                        {cajaMode === 'cobro' ? (
                          <RowAction
                            label={t('expenses.adjustBox')}
                            icon={<PointOfSaleOutlined fontSize="small" />}
                            allowed={canAdjustBox}
                            deniedLabel={t('expenses.noPermissionAdjustBox')}
                            disabled={!checked}
                            disabledLabel={t('expenses.checkRouteFirst')}
                            onClick={() => OpenModalBox(route)}
                          />
                        ) : null}
                        <RowAction
                          label={cajaMode === 'seguro' ? t('expenses.registerInsuranceExpense') : t('expenses.registerExpenses')}
                          icon={<PlaylistAddOutlined fontSize="small" />}
                          color="primary"
                          allowed={cajaMode === 'seguro' ? canManageInsuranceBox : canRegisterExpenses}
                          deniedLabel={cajaMode === 'seguro' ? t('expenses.noPermissionInsuranceBox') : t('expenses.noPermissionRegister')}
                          disabled={!checked || (cajaMode === 'seguro' && !getCashBoxByMode(route, 'seguro'))}
                          disabledLabel={!checked ? t('expenses.checkRouteFirst') : undefined}
                          onClick={() => handleOpenModal(route)}
                        />
                        <RowAction
                          label={t('expenses.clearRoute')}
                          icon={<ClearAllOutlined fontSize="small" />}
                          onClick={() => handleClearExpenses(route.id)}
                        />
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mt: 1, pl: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" component="div">{t('expenses.value')}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(routeTotal(route.id))}</Typography>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" component="div">{t('expenses.available')}</Typography>
                          <Typography variant="body2">{formatCurrency(getCashBoxByMode(route, cajaMode)?.balance ?? 0)}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </>
          ) : (
            <EmptyState dense icon={<AltRouteOutlined />} title={officeId ? t('expenses.noRoutes') : t('expenses.selectOfficeFirst')} />
          )}
        </Paper>

        <Box sx={formActionsSx}>
          <Button variant="outlined" component={NavLink} to="/loans/expenses">
            {t('common.cancel')}
          </Button>
          <LoadingButton type="submit" variant="contained" loading={saving}>
            {t('common.save')}
          </LoadingButton>
        </Box>
      </Box>
      <RouteDetailModal
        open={modalOpen}
        handleClose={handleCloseModal}
        officeRoutes={officeRoutes}
        routeCheckbox={routeCheckbox}
        handleCheckboxChange={handleCheckboxChange}
        route={selectedRoute}
        expenses={
          selectedRoute
            ? routeExpenses[selectedRoute.id]
              ? expenses.map((exp) => ({
                ...exp,
                value: routeExpenses[selectedRoute.id][exp.id]?.value || 0,
              }))
              : expenses
            : []
        }
        setValue={(value, expenseId) => {
          if (selectedRoute) {
            const typeOperationId = expenses.find(exp => exp.id === expenseId)?.operationType?.id || 2;
            const targetBoxId = getCashBoxByMode(selectedRoute, cajaMode)?.id;
            if (targetBoxId) {
              handleSetExpenseValue(selectedRoute.id, expenseId, value, targetBoxId, typeOperationId);
            }
          }
        }}
        isEditable={selectedRoute ? routeCheckbox[selectedRoute.id] || false : false}
      />
      <RouteCashBoxModal
        open={modalOpenBox}
        handleClose={CloseModalBox}
        route={selectedRoute}
        expenses={selectedRoute ? (routeExpenses[selectedRoute.id] ? expensesBox.map(exp => ({ ...exp, value: routeExpenses[selectedRoute.id][exp.id]?.value || 0 })) : expensesBox) : []}
        selectedExpenses={selectedRoute ? selectedExpensesByRoute[selectedRoute.id] || [] : []}
        onSelectedExpensesChange={(expenseIds) => {
          if (selectedRoute) {
            handleSelectedExpensesChange(selectedRoute.id, expenseIds);
          }
        }}
        setValue={(value, expenseId) => {
          if (selectedRoute) {
            const typeOperationId = expensesBox.find(exp => exp.id === expenseId)?.operationType?.id || 2;
            const targetBoxId = getCashBoxByMode(selectedRoute, 'cobro')?.id;
            if (targetBoxId) {
              SetExpenseValueBox(selectedRoute.id, expenseId, value, targetBoxId, typeOperationId);
            }
          }
        }}
        isEditable={selectedRoute ? routeCheckbox[selectedRoute.id] || false : false}
      />
    </Box>
  );
};

export default ExpenseNew;
